import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Navigation, StickyNote } from 'lucide-react';
import { getClientDisplayName } from '@shared/utils';
import { mapAPI } from '@shared/apiClient';
import { getDirectionsUrl } from './DetailSection';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const blueIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function parseCoord(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return Number.isFinite(n) ? n : null;
}

function extractMarkerData(serviceCall, isPriority) {
  let lat = null;
  let lng = null;
  let name = '';
  let address = '';
  let dirParams = {};
  let techNote = '';

  if (serviceCall.isFranklin && serviceCall.franklinSite) {
    const site = serviceCall.franklinSite;
    lat = parseCoord(site.latitude);
    lng = parseCoord(site.longitude);
    name = `Franklin - ${site.locationName || site.siteId || 'Site'}`;
    address = [site.address, site.city, site.state, site.zipCode].filter(Boolean).join(', ');
    dirParams = { latitude: site.latitude, longitude: site.longitude, address: site.address, city: site.city, state: site.state, zipCode: site.zipCode };
    techNote = site.notes || '';
  } else if (serviceCall.isCspire && serviceCall.cspireWorkOrder) {
    const wo = serviceCall.cspireWorkOrder;
    const site = wo.site;
    if (site) {
      lat = parseCoord(site.latitude);
      lng = parseCoord(site.longitude);
      address = [site.addressLine1, site.city, site.state, site.zipCode].filter(Boolean).join(', ');
      dirParams = { latitude: site.latitude, longitude: site.longitude, address: site.addressLine1, city: site.city, state: site.state, zipCode: site.zipCode };
      techNote = site.notes || '';
    }
    name = `CSpire - ${wo.locationName || site?.locationName || 'Site'}`;
  } else if (serviceCall.client) {
    const client = serviceCall.client;
    lat = parseCoord(client.latitude);
    lng = parseCoord(client.longitude);
    name = getClientDisplayName(client);
    address = [client.address, client.city, client.state, client.zipCode].filter(Boolean).join(', ');
    dirParams = { latitude: client.latitude, longitude: client.longitude, address: client.address, city: client.city, state: client.state, zipCode: client.zipCode };
    techNote = client.notes || '';
  }

  if (!name && !address) return null;

  return { id: serviceCall.id, lat, lng, name, address, isPriority, dirParams, techNote, needsGeocode: (lat === null || lng === null) && address.length > 0 };
}

const geocodeCache = new Map();

// Server-side geocoding (multi-provider with fallbacks); cached per address string
async function geocodeMarker(marker) {
  if (geocodeCache.has(marker.address)) return geocodeCache.get(marker.address);

  let result = null;
  try {
    const { address, city, state, zipCode } = marker.dirParams;
    const res = await mapAPI.geocodeAddress({ address, city, state, zipCode });
    const coords = res.data?.result;
    if (coords) {
      result = { lat: coords.latitude, lng: coords.longitude };
    }
  } catch {
    // geocoding failed silently
  }
  geocodeCache.set(marker.address, result);
  return result;
}

function FitBounds({ markers }) {
  const map = useMap();
  const prevKeyRef = useRef('');

  useEffect(() => {
    if (markers.length === 0) return;
    const key = markers.map(m => `${m.lat},${m.lng}`).join('|');
    if (key === prevKeyRef.current) return;
    prevKeyRef.current = key;

    const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [markers, map]);

  return null;
}

const MS_CENTER = [32.35, -89.4];

export default function AssignmentsMap({ priorityAssignments = [], assignments = [] }) {
  const [geocodedCoords, setGeocodedCoords] = useState({});

  const rawMarkers = useMemo(() => {
    const result = [];
    for (const sc of priorityAssignments) {
      const m = extractMarkerData(sc, true);
      if (m) result.push(m);
    }
    for (const sc of assignments) {
      const m = extractMarkerData(sc, false);
      if (m) result.push(m);
    }
    return result;
  }, [priorityAssignments, assignments]);

  const geocodeNeeded = useMemo(
    () => rawMarkers.filter(m => m.needsGeocode && !geocodedCoords[m.id]),
    [rawMarkers, geocodedCoords]
  );

  const geocodeInProgress = useRef(false);

  const runGeocode = useCallback(async () => {
    if (geocodeInProgress.current || geocodeNeeded.length === 0) return;
    geocodeInProgress.current = true;

    const results = {};
    for (const m of geocodeNeeded) {
      // The backend throttles upstream providers, so no client-side delay needed
      const coords = await geocodeMarker(m);
      if (coords) results[m.id] = coords;
    }

    if (Object.keys(results).length > 0) {
      setGeocodedCoords(prev => ({ ...prev, ...results }));
    }
    geocodeInProgress.current = false;
  }, [geocodeNeeded]);

  useEffect(() => {
    runGeocode();
  }, [runGeocode]);

  const markers = useMemo(() => {
    return rawMarkers
      .map(m => {
        if (m.lat !== null && m.lng !== null) return m;
        const gc = geocodedCoords[m.id];
        if (gc) return { ...m, lat: gc.lat, lng: gc.lng };
        return null;
      })
      .filter(Boolean);
  }, [rawMarkers, geocodedCoords]);

  const center = markers.length > 0 ? [markers[0].lat, markers[0].lng] : MS_CENTER;
  const pendingCount = geocodeNeeded.length;

  return (
    <div className="card overflow-hidden relative" style={{ height: 'calc(100vh - 320px)', minHeight: 400 }}>
      {pendingCount > 0 && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] rounded-full bg-white/90 border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-md backdrop-blur-sm">
          Locating {pendingCount} address{pendingCount !== 1 ? 'es' : ''}...
        </div>
      )}
      <MapContainer
        center={center}
        zoom={8}
        className="h-full w-full"
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds markers={markers} />
        {markers.map((m) => (
          <Marker key={m.id} position={[m.lat, m.lng]} icon={m.isPriority ? redIcon : blueIcon}>
            <Popup>
              <div className="min-w-[180px]">
                <p className="font-bold text-sm text-gray-900 mb-1">{m.name}</p>
                {m.isPriority && (
                  <span className="inline-block mb-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                    Repair
                  </span>
                )}
                {m.address && <p className="text-xs text-gray-600 mb-2">{m.address}</p>}
                {m.techNote && (
                  <div className="mb-2 flex items-start gap-1.5 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1.5 text-xs text-yellow-900">
                    <StickyNote className="h-3.5 w-3.5 mt-0.5 text-yellow-700 flex-shrink-0" />
                    <span className="whitespace-pre-wrap break-words">
                      <span className="font-semibold">Tech Note:</span> {m.techNote}
                    </span>
                  </div>
                )}
                {(() => {
                  const dirUrl = getDirectionsUrl(m.dirParams);
                  return dirUrl ? (
                    <a
                      href={dirUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-600 no-underline"
                    >
                      <Navigation className="h-3 w-3" />
                      Directions
                    </a>
                  ) : null;
                })()}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
